// src/hooks/useFolderApi.tsx

import { Storage } from 'aws-amplify'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'

import { AwsUploadResult } from '@/types/aws'
import {
  FolderRecord,
  FolderContractView,
  FolderSingleResponse,
  FolderIncludedResource,
  FolderPool,
  FolderCollectionResponse,
  FolderSingle,
  FolderId,
  FolderName,
  KindOfUse,
  FolderShortName,
} from '@/types/domain/folder'
import { SensorRecord, SensorId, SensorPool } from '@/types/domain/sensor'
import { UserRecord } from '@/types/domain/user'
import { CreatedAt, UpdatedAt } from '@/types/karma/date'

import {
  authHeader,
  notifyError,
  notifySuccess,
  categoryByType,
  userLocales,
  DDMMYYYYHHMM,
  FILTER_TYPE_COACH,
  FILTER_TYPE_CONTRACT,
  FILTER_TYPE_FOLDER,
  FILTER_TYPE_HELP_LOCATION,
  FILTER_TYPE_INSTALLATION,
  FILTER_TYPE_NEWS,
  FILTER_TYPE_OFFICE_HOURS,
  FILTER_TYPE_SENSOR,
  FILTER_TYPE_USER,
  DMMMMYYYY,
} from '../api/'
import defaultImage from '../assets/img/image_placeholder.jpg'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'
import { useContractApi } from './useContractApi'
import { useSensorPool } from './useSensorApi'

const { REACT_APP_API_URL } = import.meta.env

// context

type FolderContextType = {
  data: FolderPool
  byId: Record<string, FolderSingle>
  setData: Dispatch<SetStateAction<FolderPool>>
  refresh: (_?: { force?: boolean }) => Promise<FolderPool>
  getById: (
    _id: number,
    _opts?: { force?: boolean }
  ) => Promise<FolderSingle | null>
}

const FolderCtx = createContext<FolderContextType>({
  data: [],
  byId: {},
  setData: () => {},
  refresh: async () => [],
  getById: async () => null,
})

type FolderDataProviderProps = {
  children: ReactNode
}

export const FolderDataProvider = ({ children }: FolderDataProviderProps) => {
  const { start, stop } = usePreloader()

  const [folders, setFolders] = useState<FolderPool>([])
  const [byId, setById] = useState<Record<string, FolderSingle>>({})

  const foldersReqRef = useRef<Promise<FolderPool> | null>(null)
  const folderReqsRef = useRef<
    Record<string, Promise<FolderSingle | null> | null>
  >({})

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        return await fn()
      } catch (error) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          try {
            await refreshToken()
            return await fn()
          } catch (e2) {
            notifyError(`FolderApi: ${e2?.message}`)
            return null
          }
        }
        notifyError(`FolderApi: ${error?.message}`)
        return null
      }
    },
    []
  )

  const withPreloader = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const fetchFolders = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (foldersReqRef.current && !force) return foldersReqRef.current

      foldersReqRef.current = (async () => {
        await refreshToken()

        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/folders/admin/folders`
              const options = { headers: { ...authHeader() }, method: 'GET' }
              const response = await fetch(url, options)
              if (!response.ok)
                throw new Error(`GET folders ${response.status}`)

              const json: FolderCollectionResponse = await response.json()
              return (json?.data as FolderPool) ?? []
            })
          )

          if (Array.isArray(data)) setFolders(data)
          return Array.isArray(data) ? data : []
        } finally {
          foldersReqRef.current = null
        }
      })()

      return foldersReqRef.current
    },
    [withRequest, withPreloader]
  )

  const fetchFolderById = useCallback(
    async (
      id: FolderId,
      { force = false }: { force?: boolean } = {}
    ): Promise<FolderSingle | null> => {
      if (!id || id <= 0) return null
      const key = String(id)
      if (folderReqsRef.current[key] && !force)
        return folderReqsRef.current[key]

      folderReqsRef.current[key] = (async () => {
        const includes = [
          'coaches',
          'users',
          'office_hours',
          'exception',
          'news',
          'help_locations',
          'sensors',
          'subfolders',
          'push_sensors',
          'installations',
          'contracts',
        ]
        const query =
          includes.length > 0
            ? `?include=${includes.map(encodeURIComponent).join(',')}`
            : ''

        await refreshToken()

        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/folders/admin/folders/${id}${query}`
              const options = { headers: { ...authHeader() }, method: 'GET' }
              const response = await fetch(url, options)
              if (!response.ok) throw new Error(`GET folder ${response.status}`)

              const item: FolderSingleResponse = await response.json()
              const included: FolderIncludedResource[] = item.included ?? []
              const contractResource = included.find(FILTER_TYPE_CONTRACT)
              const sensorRel = contractResource?.relationships?.sensors?.data

              const begin: string =
                contractResource?.attributes.date_begin || ''
              const expire: string =
                contractResource?.attributes.date_expire || ''

              const sensorIds: SensorId[] = Array.isArray(sensorRel)
                ? sensorRel.map(r => Number(r.id))
                : sensorRel
                  ? [Number((sensorRel as { id: string }).id)]
                  : []

              const fmtDate = (date: string | number | Date) =>
                new Date(date).toLocaleDateString(userLocales, DMMMMYYYY)
              const isValid = (d: Date) => !isNaN(d.getTime())

              const BEGIN: Date = new Date(begin)
              const EXPIRE: Date = new Date(expire)
              const NOW: Date = new Date()

              const status: string =
                !isValid(BEGIN) || !isValid(EXPIRE) || BEGIN > EXPIRE
                  ? 'ungültig'
                  : NOW.getTime() > EXPIRE.getTime()
                    ? 'abgelaufen'
                    : NOW.getTime() < BEGIN.getTime()
                      ? 'geplant'
                      : NOW.getTime() > BEGIN.getTime() &&
                          NOW.getTime() < EXPIRE.getTime()
                        ? 'aktiv'
                        : `unvorhergesehen: ${fmtDate(begin)} - ${fmtDate(expire)}`

              const contract: FolderContractView | null = contractResource
                ? { ...contractResource.attributes, sensorIds, status }
                : null

              const result = {
                ...item.data.attributes,
                coaches: included
                  .filter(FILTER_TYPE_COACH)
                  .map(c => c.attributes),
                contract,
                folders: included
                  .filter(FILTER_TYPE_FOLDER)
                  .map(f => f.attributes),
                help_locations: included
                  .filter(FILTER_TYPE_HELP_LOCATION)
                  .map(h => h.attributes),
                installations: included
                  .filter(FILTER_TYPE_INSTALLATION)
                  .map(i => i.attributes),
                news: included.filter(FILTER_TYPE_NEWS).map(n => n.attributes),
                office_hours: included
                  .filter(FILTER_TYPE_OFFICE_HOURS)
                  .map(o => o.attributes),
                sensors: included
                  .filter(FILTER_TYPE_SENSOR)
                  .map(s => s.attributes),
                sensorIds: included
                  .filter(FILTER_TYPE_SENSOR)
                  .map(s => Number(s.id))
                  .sort((a: number, b: number) => a - b),
                users: included.filter(FILTER_TYPE_USER).map(u => u.attributes),
              } satisfies FolderSingle

              setById(prev => ({ ...prev, [key]: result }))
              return result
            })
          )

          return data || null
        } finally {
          folderReqsRef.current[key] = null
        }
      })()

      return folderReqsRef.current[key]
    },
    [withRequest, withPreloader]
  )

  const didInitRef = useRef(false)

  useEffect(() => {
    if (didInitRef.current) return
    didInitRef.current = true
    fetchFolders()
  }, [fetchFolders])

  const foldersValue = useMemo(
    () => ({
      data: folders,
      byId: byId,
      setData: setFolders,
      refresh: fetchFolders,
      getById: fetchFolderById,
    }),
    [folders, byId, fetchFolders, fetchFolderById]
  )

  return (
    <FolderCtx.Provider value={foldersValue}>{children}</FolderCtx.Provider>
  )
}

// pool

export const useFolderPool = (): FolderPool => {
  const ctx = useContext(FolderCtx)
  if (!ctx) return []
  if (!ctx.data) return []

  return ctx.data
}
// api

export const useFolderApi = () => {
  const { refresh: refreshFolders } = useContext(FolderCtx)!
  const { save: saveContract } = useContractApi()

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        return await fn()
      } catch (error) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          try {
            await refreshToken()
            return await fn()
          } catch (e2) {
            notifyError(`FolderAPI: ${e2?.message}`)
            return null
          }
        }
        notifyError(`FolderAPI: ${error?.message}`)
        return null
      }
    },
    []
  )

  const uploadAws = useCallback(async (file: File, dirName: string) => {
    const handle = new File(
      [file],
      (
        Math.random().toString(36).substring(2, 16) +
        Math.random().toString(36).substring(2, 16)
      ).toUpperCase() +
        Date.now() +
        file.name.slice(file.name.lastIndexOf('.'))
    )

    const key = `${dirName}/${handle.name}`

    await Storage.put(key, handle, {
      contentType: file.type,
      customPrefix: { public: '' },
      level: 'public',
    })

    return { location: key }
  }, [])

  const recordData = useCallback(
    (data: { folder: Partial<FolderRecord> & Record<string, unknown> }) => {
      const {
        air_conditioning,
        area_in_sqm,
        city,
        country,
        elevator,
        escalator,
        folder_installations,
        hired_out_area,
        image_url,
        kind_of_use,
        kpi_benchmark,
        latitude,
        longitude,
        name,
        name_short,
        none_temperature_controlled_area,
        number_of_employees,
        parent_id,
        push_sensors,
        rooms_mode,
        sensor_id,
        street,
        target_value_annual_electricity_consumption,
        target_value_annual_gas_consumption,
        temperature_controlled_area,
        total_consumption_available,
        usable_area,
        user_id,
        zip,
        energy_benchmark,
        water_benchmark,
        gas_benchmark,
      } = data.folder

      return {
        data: {
          attributes: {
            air_conditioning: air_conditioning || null,
            area_in_sqm: area_in_sqm ?? null,
            city: city || null,
            country: country || null,
            elevator: elevator ?? null,
            escalator: escalator || null,
            hired_out_area: hired_out_area || null,
            image_url: image_url || null,
            kind_of_use: kind_of_use || null,
            kpi_benchmark: kpi_benchmark || null,
            latitude: latitude || null,
            longitude: longitude || null,
            name: name || null,
            name_short: name_short || null,
            none_temperature_controlled_area:
              none_temperature_controlled_area || null,
            number_of_employees: number_of_employees || null,
            parent_id: parent_id ?? null,
            rooms_mode: rooms_mode || null,
            street: street || null,
            target_value_annual_electricity_consumption:
              target_value_annual_electricity_consumption || null,
            target_value_annual_gas_consumption:
              target_value_annual_gas_consumption || null,
            temperature_controlled_area: temperature_controlled_area || null,
            total_consumption_available: total_consumption_available ?? null,
            usable_area: usable_area || null,
            zip: zip || null,
            energy_benchmark: energy_benchmark || null,
            water_benchmark: water_benchmark || null,
            gas_benchmark: gas_benchmark || null,
          },
          relationships: {
            installations: {
              data: (Array.isArray(folder_installations)
                ? folder_installations
                : []
              ).map(item => ({
                type: 'installation',
                name: item.name,
                entity_id: item.entity_id,
                delete_installation: item.delete_installation
                  ? item.delete_installation
                  : 0,
              })),
            },
            push_sensors: {
              data: (Array.isArray(push_sensors) ? push_sensors : []).map(
                item => ({
                  type: 'push_sensor',
                  id: item.value,
                })
              ),
            },
            sensors: {
              data: (Array.isArray(sensor_id) ? sensor_id : []).map(item => ({
                type: 'sensor',
                id: item.value,
              })),
            },
            users: {
              data: (Array.isArray(user_id) ? user_id : []).map(item => ({
                type: 'user',
                id: item.value,
              })),
            },
          },
        },
      }
    },
    []
  )

  const saveOfficeHours = useCallback(
    async (hours: any[], exceptions: any[], id: FolderId) =>
      withRequest(async () => {
        const safeExceptions = Array.isArray(exceptions) ? exceptions : []
        const exceptionsPayload = safeExceptions.map(item => {
          const { closed, date_exception, end_time, name, start_time } = item
          return {
            data: {
              attributes: {
                folder_id: id,
                day_of_week: null,
                name,
                date_exception,
                start_time: start_time ? start_time : '00:00',
                end_time: end_time ? end_time : '00:00',
                closed,
                exception: 1,
              },
            },
          }
        })

        const officeHoursPayload = (hours || []).map(item => {
          const { closed, day_of_week, end_time, start_time } = item
          return {
            data: {
              attributes: {
                folder_id: id,
                day_of_week,
                name: null,
                date_exception: null,
                start_time: start_time ? start_time : '00:00',
                end_time: end_time ? end_time : '00:00',
                closed,
                exception: 0,
              },
            },
          }
        })

        const body = {
          collection: officeHoursPayload.concat(exceptionsPayload),
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/officehours/admin/officehours`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(body),
        }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }

        return await response.json()
      }),
    [withRequest]
  )

  const save = useCallback(
    async (
      data: {
        folder: Partial<FolderRecord>
        officeHours?: unknown[]
        exceptionDate?: unknown[]
      },
      contractRecord: unknown
    ) =>
      withRequest(async () => {
        const { folder } = data || {}
        const { image_url, name } = folder as {
          image_url?: string | File
          name?: string
        }
        if (!name) {
          notifyError(LC.FOLDER_NAME_REQUIRED)
          return null
        }

        let folderData = { ...data }
        if (
          image_url &&
          typeof image_url === 'object' &&
          image_url instanceof File
        ) {
          const upload = (await uploadAws(
            image_url,
            'folders'
          )) as AwsUploadResult
          const uploadedUrl = upload.location
          folderData = {
            ...data,
            folder: { ...data.folder, image_url: uploadedUrl },
          }
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/folders/admin/folders`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(recordData(folderData)),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }
        const result = await response.json()
        const id = result?.id

        const { officeHours = [], exceptionDate = [] } = data
        if (officeHours.length > 0 || exceptionDate.length > 0)
          await saveOfficeHours(officeHours, exceptionDate, id)

        if (contractRecord) saveContract(contractRecord)

        notifySuccess(LC.FOLDER_SAVED)

        await refreshFolders({ force: true })
        return id
      }),
    [
      recordData,
      saveContract,
      saveOfficeHours,
      uploadAws,
      refreshFolders,
      withRequest,
    ]
  )

  const update = useCallback(
    async (data: {
      id: FolderId
      folder: Partial<FolderRecord>
      officeHours?: unknown[]
      exceptionDate?: unknown[]
    }) =>
      withRequest(async () => {
        const { folder, id } = data || {}
        const { image_url, name } = folder as {
          image_url?: string | File
          name?: string
        }

        if (!id) {
          notifyError(LC.FOLDER_ID_REQUIRED)
          return null
        }
        if (!name) {
          notifyError(LC.FOLDER_NAME_REQUIRED)
          return null
        }

        let folderData = { ...data }
        if (
          image_url &&
          typeof image_url === 'object' &&
          image_url instanceof File
        ) {
          const upload = (await uploadAws(
            image_url,
            'folders'
          )) as AwsUploadResult
          const uploadedUrl = upload.location
          folderData = {
            ...data,
            folder: { ...data.folder, image_url: uploadedUrl },
          }
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/folders/admin/folders/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'PATCH',
          body: JSON.stringify(recordData(folderData)),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }

        const { officeHours = [], exceptionDate = [] } = data
        if ((officeHours && officeHours.length > 0) || exceptionDate.length > 0)
          await saveOfficeHours(officeHours, exceptionDate, id)

        notifySuccess(LC.FOLDER_UPDATED)

        await refreshFolders({ force: true })
        return id
      }),
    [recordData, saveOfficeHours, uploadAws, refreshFolders, withRequest]
  )

  const remove = useCallback(
    async (id: FolderId) =>
      withRequest(async () => {
        if (!id) {
          notifyError(LC.FOLDER_ID_REQUIRED)
          return false
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/folders/admin/folders/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'DELETE',
        }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }

        notifySuccess(LC.FOLDER_DELETED)

        await refreshFolders({ force: true })
        return true
      }),
    [refreshFolders, withRequest]
  )

  return {
    save,
    update,
    remove,
    refreshFolders,
  }
}

export const useFolder = (id: FolderId): FolderSingle | null => {
  const { byId, getById } = useContext(FolderCtx)!
  const key = String(id || '')
  const folder = key ? byId[key] || null : null

  useEffect(() => {
    if (!id) return
    if (!folder) getById(id)
  }, [id, folder, getById])

  return folder
}

export const useBridgeId = (id: FolderId): SensorId => {
  const { data = [] } = useContext(FolderCtx)
  const [bridge, setBridge] = useState<SensorId>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const b = record?.attributes?.folder_sensors?.[0]?.sensor_id
    if (!b) return

    setBridge(b)
  }, [data, id])

  return bridge
}

export const useCurrentFolder = (): FolderId => {
  const { pathname } = useLocation()

  const [id, setId] = useState<FolderId>(0)

  useEffect(() => {
    const getFolder = () => {
      const match = pathname.match(/\/folder\/(\d+)/)
      const fById: FolderId = match ? Number(match[1]) : 0
      if (!fById) return

      setId(fById)
    }
    getFolder()
  }, [pathname])

  return id
}

export const useFolderAddress = (id: FolderId): string => {
  const { data = [] } = useContext(FolderCtx)
  const [address, setAddress] = useState<string>('')

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const city: string = record?.attributes?.city
    const street: string = record?.attributes?.street
    const zip: string = record?.attributes?.zip
    if (!city) return

    setAddress(`${street}, ${zip} ${city}`)
  }, [data, id])

  return address
}

export const useFolderArea = (id: FolderId) => {
  const { data = [] } = useContext(FolderCtx)
  const [area, setArea] = useState<number | null>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const a = record?.attributes?.area_in_sqm
    if (!a) return

    setArea(a)
  }, [data, id])

  return area
}

export const useFolderCity = (id: FolderId) => {
  const { data = [] } = useContext(FolderCtx)
  const [city, setCity] = useState<string>('')

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const c = record?.attributes?.city
    if (!c) return

    setCity(c)
  }, [data, id])

  return city
}

export const useFolderContract = (id: FolderId) => {
  const [data, setData] = useState<FolderContractView | null>(null)
  const fData: FolderSingle = useFolder(id)

  useEffect(() => {
    if (!id || !fData?.contract) return
    setData(fData?.contract)
  }, [fData, id])

  return data
}

export const useFolderCTime = (
  id: FolderId,
  locales = userLocales,
  options = DDMMYYYYHHMM
): CreatedAt => {
  const { data = [] } = useContext(FolderCtx)
  const [cTime, setCTime] = useState<CreatedAt>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const c: CreatedAt = record?.attributes?.created_at
    if (!c) return

    setCTime(new Date(c).toLocaleString(locales, options))
  }, [data, id, locales, options])

  return cTime
}

export const useFolderImage = (id: FolderId) => {
  const { data = [] } = useContext(FolderCtx)
  const [image, setImage] = useState<string>(defaultImage)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const value = record?.attributes?.image_url
    if (!value) return

    if (value.startsWith('http')) {
      setImage(value)
      return
    }

    const load = async () => {
      try {
        const url = await Storage.get(value, {
          level: 'public',
          customPrefix: { public: '' },
        })
        setImage(String(url))
      } catch {
        setImage(defaultImage)
      }
    }

    load()
  }, [data, id])

  return image
}

type Installation = {
  entity_id: number
}

type InstallationWithSensors = Installation & {
  sensors: SensorRecord[]
}

export const useFolderInstallations = (id: FolderId) => {
  const [data, setData] = useState<InstallationWithSensors[]>([])
  const fData: FolderSingle = useFolder(id)

  useEffect(() => {
    if (!id || !fData?.installations) return
    const sensors = fData?.sensors || []

    const installationsWithSensors = (
      fData.installations as Installation[]
    ).map(installation => {
      const installationSensors = sensors.filter(
        s => s.installation_id === installation.entity_id
      )

      return {
        ...(installation as Installation),
        sensors: installationSensors,
      }
    })

    setData(installationsWithSensors)
  }, [fData, id])

  return data
}

export const useFolderKindOfUse = (id: FolderId): KindOfUse => {
  const { data = [] } = useContext(FolderCtx)
  const [kindOfUse, setKindOfUse] = useState<KindOfUse>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const k: KindOfUse = record?.attributes?.kind_of_use
    if (!k) return

    setKindOfUse(k)
  }, [data, id])

  return kindOfUse
}

export const useFolderMTime = (
  id: FolderId,
  locales = userLocales,
  options = DDMMYYYYHHMM
): UpdatedAt => {
  const { data = [] } = useContext(FolderCtx)
  const [mTime, setMTime] = useState<UpdatedAt>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const u: UpdatedAt = record?.attributes?.updated_at
    if (!u) return

    setMTime(new Date(u).toLocaleString(locales, options))
  }, [data, id, locales, options])

  return mTime
}

export const useFolderName = (id: FolderId): FolderName => {
  const { data = [] } = useContext(FolderCtx)
  const [name, setName] = useState<FolderName>('')

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const n: FolderName = record?.attributes?.name
    if (!n) return

    setName(n)
  }, [data, id])

  return name
}

export const useFolderParent = (id: FolderId): FolderId => {
  const { data = [] } = useContext(FolderCtx)
  const [parent, setParent] = useState<FolderId>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const p = record?.attributes?.parent_id
    if (!p) return

    setParent(p)
  }, [data, id])

  return parent
}

export const useFolderSensors = (id: FolderId) => {
  const fData: FolderSingle = useFolder(id)
  const [data, setData] = useState<SensorRecord[]>([])

  useEffect(() => {
    if (fData?.sensors) setData(fData.sensors)
  }, [id, fData])

  return data
}

export const useFolderSensorIds = (id: FolderId) => {
  const { data: fPool = [] } = useContext(FolderCtx)
  const [data, setData] = useState<SensorId[]>([])

  useEffect(() => {
    if (!id) return

    const record = fPool.find(f => Number(f.id) === id)
    if (!record?.attributes?.folder_sensors) return

    const fSensors = record.attributes.folder_sensors
      .map(f => Number(f.sensor_id))
      .sort((a, b) => a - b)

    if (record) setData(fSensors)
  }, [fPool, id])

  return data
}

export const useFolderShortName = (id: FolderId): FolderShortName => {
  const { data = [] } = useContext(FolderCtx)
  const [name, setName] = useState<FolderShortName>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const n: FolderShortName = record?.attributes?.name_short
    if (!n) return

    setName(n)
  }, [data, id])

  return name
}

export type FolderStatus = {
  correlation: string | null
  feeling: string | null
  installation: string | null
  performance: string | null
  security: string | null
  canCorrelate: boolean | null
}

export const useFolderStatus = (id: FolderId) => {
  const [data, setData] = useState<FolderStatus>({
    correlation: null,
    feeling: null,
    installation: null,
    performance: null,
    security: null,
    canCorrelate: null,
  })

  const ctx = useContext(FolderCtx)
  const fPool = useMemo(() => ctx?.data ?? [], [ctx])
  const sPool: SensorPool = useSensorPool()

  const filters = useMemo(
    () => ({
      isCurrentFolder: f => Number(f.attributes.folder_id) === id,
      isConsumptionSensor: f =>
        ['consumption', 'performance'].includes(
          categoryByType(f.attributes.sensor_type)
        ),
      isSecuritySensor: f =>
        f.attributes.sensor_target === 'security' ||
        categoryByType(f.attributes.sensor_type) === 'security',
      isFeelingSensor: f =>
        f.attributes.sensor_target === 'feeling' ||
        categoryByType(f.attributes.sensor_type) === 'feeling',
      canCorrelate: f => {
        const blacklist = [
          'door',
          'smokeDetektor',
          'Removal_detection',
          'Battery_end_of_life',
          'Smoke_alarm',
          'Acoustic_alarm_failure',
          'waterDetected',
          'external_digital_button',
          'external_digital_button_2',
        ]
        return !blacklist.includes(f.attributes.sensor_type)
      },
    }),
    [id]
  )

  const filterData = useCallback(
    (fn: (_) => boolean) =>
      sPool.length === 0
        ? []
        : sPool.filter(filters.isCurrentFolder).filter(fn),
    [sPool, filters.isCurrentFolder]
  )

  useEffect(() => {
    if (!id || !sPool) return

    const record = fPool.find(f => Number(f.id) === id)
    if (!record) return

    const { sensor_group_status: sgs } = record.attributes

    const getStatus = (category: string) =>
      sgs.find(f => f.type === category)?.status || 'neutral'

    const P_SENSORS = filterData(filters.isConsumptionSensor)
    const S_SENSORS = filterData(filters.isSecuritySensor)
    const W_SENSORS = filterData(filters.isFeelingSensor)

    const performance =
      P_SENSORS.length > 0 ? getStatus('status_performance') : 'neutral'

    const security =
      S_SENSORS.length > 0 ? getStatus('status_security') : 'neutral'

    const feeling =
      W_SENSORS.length > 0 ? getStatus('status_well_being') : 'neutral'

    const PSW_SENSORS = [...P_SENSORS, ...S_SENSORS, ...W_SENSORS]

    const correlation =
      PSW_SENSORS.filter(filters.canCorrelate).length > 1 ? 'green' : 'neutral'

    const installation = getStatus('status_installation')

    const canCorrelate =
      PSW_SENSORS.length > 0
        ? PSW_SENSORS.filter(filters.canCorrelate).length > 1
        : null

    setData({
      performance,
      security,
      feeling,
      correlation,
      installation,
      canCorrelate,
    })
  }, [fPool, id, filterData, filters, sPool])

  return data
}

export const useFolderTCA = (id: FolderId) => {
  const { data: fPool = [] } = useContext(FolderCtx)
  const [data, setData] = useState<boolean | null>(null)

  useEffect(() => {
    if (!id) return

    const record = fPool.find(f => Number(f.id) === id)
    if (!record) return

    const total_consumption_available = Boolean(
      record?.attributes?.total_consumption_available
    )

    setData(total_consumption_available)
  }, [fPool, id])

  return data
}

export const useFolderUsers = (id: FolderId): UserRecord[] => {
  const fData: FolderSingle = useFolder(id)
  const [data, setData] = useState([])

  useEffect(() => {
    if (fData?.users) setData(fData?.users)
  }, [id, fData])

  return data
}

export const useRoomsMode = (id: FolderId): string => {
  const { data = [] } = useContext(FolderCtx)
  const [roomsMode, setRoomsMode] = useState<string>('Mitarbeiter')

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const mode = record?.attributes?.rooms_mode
    if (!mode) return

    switch (mode) {
      case 'employees':
        setRoomsMode('Mitarbeiter')
        break
      case 'rooms':
        setRoomsMode('Zimmer')
        break
      default:
        setRoomsMode('Mitarbeiter')
        break
    }
  }, [data, id])

  return roomsMode
}
